import { LocalAtRecord } from '../../db';
import { BoxesFieldSet } from '@rogoag/airtable/crm';
import { Card, CardContent, Grid, Typography, Button, Collapse, IconButton } from '@mui/material';
import { AiOutlineMinusCircle } from 'react-icons/ai';
import { ExpandMore, ExpandLess, CheckCircle } from '@mui/icons-material';
import TakePhotoOrUpload from './TakePhotoOrUpload';
import NoScanOrPicExcuse from './NoScanOrPicExcuse';

const warningButtonSx = {
  color: '#ff9800', // orange
  borderColor: '#ff9800',
  '&:hover': {
    backgroundColor: 'rgba(255,152,0,0.08)', // light orange hover
    borderColor: '#ff9800',
  },
} as const;

interface Props {
  box: LocalAtRecord<BoxesFieldSet>;
  excludedBoxes: string[];
  expandedBoxes: string[];
  group: any;
  dropoffName: string;
  collapseBox: (boxId: string) => void;
  expandBox: (boxId: string) => void;
  excludeBox: (boxId: string) => void;
  scannedBoxIds: string[];
  cameraError: string;
  scannedBoxPicture?: string;
  activateCaptureModal: () => void;
  onUploadedBoxPictureChange: (boxId: string, file: File) => Promise<void>;
  loading: boolean;
  captureLoading: boolean;
  noScanOrPicExcuse?: string;
  onNoScanOrPicExcuseChange: (excuse: string | undefined) => void;
}

export default function ShippingGroupBoxCard({
  box,
  excludedBoxes,
  expandedBoxes,
  group,
  dropoffName,
  collapseBox,
  expandBox,
  excludeBox,
  scannedBoxIds,
  cameraError,
  scannedBoxPicture,
  activateCaptureModal,
  onUploadedBoxPictureChange,
  loading,
  captureLoading,
  noScanOrPicExcuse,
  onNoScanOrPicExcuseChange,
}: Props) {
  const boxId = box.get('Box ID') ?? '';
  const isScanned = scannedBoxIds.includes(boxId);
  const boxValid = group.get(dropoffName).valid;
  const isExpanded = expandedBoxes.includes(boxId);

  if (excludedBoxes.includes(boxId)) {
    return null;
  }

  return (
    <Card style={{ marginBottom: 16, minHeight: 305, opacity: boxValid ? 1 : 0.25 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center" style={{ minHeight: 53 }} size={12}>
          <Grid size="grow">
            <Grid container alignItems="center" spacing={1} size={12}>
              <Grid>
                <IconButton size="small" onClick={() => (isExpanded ? collapseBox(boxId) : expandBox(boxId))}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Grid>

              <Grid>
                <Typography
                  variant="body2"
                  color={isScanned ? 'primary' : 'error'}
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    color: isScanned ? 'green' : undefined,
                  }}
                >
                  Box {boxId}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <Grid container alignItems="center" spacing={1} size={12}>
              <Grid>
                {isScanned ? (
                  <Typography variant="body2">Scanned</Typography>
                ) : (
                  <Typography variant="body2" color="error" style={{ fontStyle: 'italic' }}>
                    Needs scanning
                  </Typography>
                )}
              </Grid>

              {isScanned && (
                <Grid style={{ marginBottom: 7 }}>
                  <CheckCircle style={{ color: '#4caf50', fontSize: 20 }} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="space-between" style={{ marginTop: 12 }} size={12}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Picture: use only if scanning fails
            </Typography>
          </Grid>

          <Grid
            style={{ width: '100%' }}
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <TakePhotoOrUpload
              cameraError={cameraError}
              picture={scannedBoxPicture}
              activateCaptureModal={activateCaptureModal}
              onUploadedFileChange={(file) => onUploadedBoxPictureChange(boxId, file)}
              loading={loading}
              captureLoading={captureLoading}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: 12 }} size={12}>
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>
              Note: use only if cannot take/upload picture
            </Typography>

            <NoScanOrPicExcuse excuse={noScanOrPicExcuse || ''} onExcuseChange={onNoScanOrPicExcuseChange} />
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: 12 }} justifyContent="center" alignItems="center" size={12}>
          <Grid>
            <Button
              variant="outlined"
              size="small"
              disabled={!boxValid}
              onClick={() => excludeBox(boxId)}
              startIcon={<AiOutlineMinusCircle />}
              sx={warningButtonSx}
            >
              Exclude box from shipping
            </Button>
          </Grid>
        </Grid>

        <Collapse in={isExpanded}>
          <Grid container spacing={1} style={{ marginTop: 16 }} size={12}>
            {Object.entries(JSON.parse(box.get('Samples') ?? '{}')).map(([key, sampleObj]: [string, any]) => (
              <Grid key={key} style={{ backgroundColor: '#f2f2f2', padding: 8 }} size={12}>
                <Typography variant="subtitle2">{sampleObj.jobInfo.field}</Typography>
                <Typography variant="body2">Samples: {sampleObj.samples.length}</Typography>
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
}
